import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  GitCompare,
  Link2,
  Download,
  Play,
  Pause,
  StopCircle,
  RefreshCw,
  ArrowLeft,
  AlertCircle,
  Clock,
  TrendingUp,
  Search,
  Image,
  Code,
  Globe,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useProject,
  useMetrics,
  useCrawlProgress,
  useStartCrawl,
  usePages,
  useCannibalization,
} from '../hooks/useSEO';
import * as seoApi from '../lib/seoApi';
import { StatCard, StatGrid } from '../components/seo/StatCard';
import { ScoreCircle } from '../components/seo/ScoreCircle';
import { CrawlProgress } from '../components/seo/ProgressBar';
import { PageTable } from '../components/seo/PageCard';
import { IssuesSummary } from '../components/seo/IssuesList';

type TabId = 'overview' | 'pages' | 'cannibalization' | 'export';

export function DashboardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const { project, loading: projectLoading, error: projectError, refetch: refetchProject } = useProject(projectId);
  const { metrics, loading: metricsLoading, refetch: refetchMetrics } = useMetrics(projectId);
  const { progress, refetch: refetchProgress } = useCrawlProgress(
    projectId,
    project?.status === 'crawling' || project?.status === 'analyzing'
  );
  const { startCrawl, loading: startingCrawl } = useStartCrawl();
  const { pages, total: totalPages, loading: pagesLoading, filters, updateFilters } = usePages(projectId, {
    page: 1,
    pageSize: 10,
    sortBy: 'seoScore',
    sortOrder: 'desc',
  });
  const { pairs: cannibalizationPairs, loading: cannLoading } = useCannibalization(projectId);

  // Refetch data when crawl completes
  useEffect(() => {
    if (progress?.job.status === 'completed') {
      refetchProject();
      refetchMetrics();
    }
  }, [progress?.job.status, refetchProject, refetchMetrics]);

  const handleStartCrawl = async () => {
    if (!projectId) return;
    try {
      await startCrawl(projectId);
      refetchProject();
    } catch (error) {
      console.error('Failed to start crawl:', error);
    }
  };

  const handleExport = async (format: 'json' | 'csv') => {
    if (!projectId) return;
    try {
      const blob = await seoApi.exportProject(projectId, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seo-audit-${projectId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-900 mb-2">Project not found</h3>
          <p className="text-surface-500 mb-6">{projectError}</p>
          <Link to="/" className="btn-primary mx-auto">
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const isCrawling = project.status === 'crawling' || project.status === 'analyzing';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link
            to="/"
            className="text-sm text-surface-500 hover:text-surface-700 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to Projects
          </Link>
          <h1 className="text-2xl font-bold text-surface-900">{project.name}</h1>
          <p className="text-surface-500 mt-1 flex items-center gap-1">
            <Globe className="w-4 h-4" />
            {project.baseUrl}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {project.status === 'pending' && (
            <button
              onClick={handleStartCrawl}
              disabled={startingCrawl}
              className="btn-primary"
            >
              <Play className="w-4 h-4" />
              {startingCrawl ? 'Starting...' : 'Start Crawl'}
            </button>
          )}

          {project.status === 'completed' && (
            <>
              <button
                onClick={() => setActiveTab('export')}
                className="btn-secondary"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={handleStartCrawl}
                disabled={startingCrawl}
                className="btn-primary"
              >
                <RefreshCw className="w-4 h-4" />
                Re-crawl
              </button>
            </>
          )}
        </div>
      </div>

      {/* Crawl Progress */}
      {isCrawling && progress && (
        <div className="card mb-6">
          <h3 className="font-semibold text-surface-900 mb-4">Crawl Progress</h3>
          <CrawlProgress
            totalUrls={progress.job.totalUrls}
            crawledUrls={progress.job.crawledUrls}
            analyzedUrls={progress.job.analyzedUrls}
            failedUrls={progress.job.failedUrls}
            status={progress.job.status}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="tabs mb-6">
        {[
          { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
          { id: 'pages' as const, label: 'Pages', icon: FileText },
          { id: 'cannibalization' as const, label: 'Cannibalization', icon: GitCompare },
          { id: 'export' as const, label: 'Export', icon: Download },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx('tab', activeTab === tab.id && 'active')}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          metrics={metrics}
          loading={metricsLoading}
          pages={pages}
          projectId={projectId!}
        />
      )}

      {activeTab === 'pages' && (
        <PagesTab
          pages={pages}
          total={totalPages}
          loading={pagesLoading}
          filters={filters}
          updateFilters={updateFilters}
          projectId={projectId!}
        />
      )}

      {activeTab === 'cannibalization' && (
        <CannibalizationTab
          pairs={cannibalizationPairs}
          loading={cannLoading}
          projectId={projectId!}
        />
      )}

      {activeTab === 'export' && (
        <ExportTab onExport={handleExport} projectName={project.name} />
      )}
    </div>
  );
}

function OverviewTab({
  metrics,
  loading,
  pages,
  projectId,
}: {
  metrics: ReturnType<typeof useMetrics>['metrics'];
  loading: boolean;
  pages: ReturnType<typeof usePages>['pages'];
  projectId: string;
}) {
  if (loading || !metrics) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <StatGrid columns={4}>
        <div className="card flex items-center gap-4">
          <ScoreCircle score={metrics.avgSeoScore} size="lg" />
          <div>
            <p className="text-sm font-medium text-surface-500">Average Score</p>
            <p className="text-xs text-surface-400 mt-1">
              {metrics.analyzedPages} of {metrics.totalPages} analyzed
            </p>
          </div>
        </div>

        <StatCard
          title="Critical Issues"
          value={metrics.criticalIssues}
          icon={AlertCircle}
          iconColor="text-red-600"
        />

        <StatCard
          title="Avg. Load Time"
          value={`${(metrics.avgLoadTime / 1000).toFixed(1)}s`}
          icon={Clock}
          iconColor="text-blue-600"
        />

        <StatCard
          title="Cannibalization"
          value={metrics.cannibalizationPairs}
          subtitle="pairs detected"
          icon={GitCompare}
          iconColor="text-yellow-600"
        />
      </StatGrid>

      {/* Score Distribution */}
      <div className="card">
        <h3 className="font-semibold text-surface-900 mb-4">Score Distribution</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="p-4 bg-green-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-600">{metrics.scoreDistribution.excellent}</p>
            <p className="text-sm text-green-700">Excellent (90+)</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-blue-600">{metrics.scoreDistribution.good}</p>
            <p className="text-sm text-blue-700">Good (70-89)</p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-yellow-600">{metrics.scoreDistribution.average}</p>
            <p className="text-sm text-yellow-700">Average (50-69)</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-red-600">{metrics.scoreDistribution.poor}</p>
            <p className="text-sm text-red-700">Poor (&lt;50)</p>
          </div>
        </div>
      </div>

      {/* Common Issues */}
      {metrics.commonIssues.length > 0 && (
        <div className="card">
          <h3 className="font-semibold text-surface-900 mb-4">Common Issues</h3>
          <div className="space-y-2">
            {metrics.commonIssues.slice(0, 5).map((issue, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-surface-50 rounded-lg"
              >
                <span className="text-sm text-surface-700">{issue.type.replace(/_/g, ' ')}</span>
                <span className="text-sm font-medium text-surface-900">{issue.count} pages</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Pages */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-surface-900">Top Pages by Score</h3>
          <Link
            to={`/projects/${projectId}/pages`}
            className="text-sm text-brand-600 hover:text-brand-700"
          >
            View all
          </Link>
        </div>
        {pages.length > 0 ? (
          <PageTable pages={pages.slice(0, 5)} projectId={projectId} />
        ) : (
          <p className="text-center py-8 text-surface-500">No pages analyzed yet</p>
        )}
      </div>
    </div>
  );
}

function PagesTab({
  pages,
  total,
  loading,
  filters,
  updateFilters,
  projectId,
}: {
  pages: ReturnType<typeof usePages>['pages'];
  total: number;
  loading: boolean;
  filters: ReturnType<typeof usePages>['filters'];
  updateFilters: ReturnType<typeof usePages>['updateFilters'];
  projectId: string;
}) {
  const [search, setSearch] = useState(filters.search || '');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilters({ search, page: 1 });
  };

  const totalPages = Math.ceil(total / (filters.pageSize || 20));
  const currentPage = filters.page || 1;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search pages by URL or title..."
              className="input pl-10"
            />
          </div>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>

        <div className="flex items-center gap-4 mt-4">
          <select
            value={filters.sortBy || 'seoScore'}
            onChange={(e) => updateFilters({ sortBy: e.target.value as any, page: 1 })}
            className="input w-auto"
          >
            <option value="seoScore">Sort by Score</option>
            <option value="url">Sort by URL</option>
            <option value="loadTime">Sort by Load Time</option>
            <option value="wordCount">Sort by Word Count</option>
          </select>

          <select
            value={filters.sortOrder || 'desc'}
            onChange={(e) => updateFilters({ sortOrder: e.target.value as any, page: 1 })}
            className="input w-auto"
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filters.hasIssues || false}
              onChange={(e) => updateFilters({ hasIssues: e.target.checked || undefined, page: 1 })}
              className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500"
            />
            Only show pages with issues
          </label>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full" />
        </div>
      ) : pages.length > 0 ? (
        <>
          <p className="text-sm text-surface-500">Showing {pages.length} of {total} pages</p>
          <PageTable pages={pages} projectId={projectId} />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => updateFilters({ page: currentPage - 1 })}
                disabled={currentPage === 1}
                className="btn-secondary btn-sm"
              >
                Previous
              </button>
              <span className="text-sm text-surface-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => updateFilters({ page: currentPage + 1 })}
                disabled={currentPage === totalPages}
                className="btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="card text-center py-12">
          <FileText className="w-12 h-12 text-surface-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-900 mb-2">No pages found</h3>
          <p className="text-surface-500">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
}

function CannibalizationTab({
  pairs,
  loading,
  projectId,
}: {
  pairs: ReturnType<typeof useCannibalization>['pairs'];
  loading: boolean;
  projectId: string;
}) {
  const getUrlPath = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (pairs.length === 0) {
    return (
      <div className="card text-center py-12">
        <GitCompare className="w-12 h-12 text-green-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-surface-900 mb-2">No Cannibalization Detected</h3>
        <p className="text-surface-500">
          Great news! No pages appear to be targeting the same keywords.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="font-semibold text-surface-900 mb-2">Keyword Cannibalization</h3>
        <p className="text-sm text-surface-500 mb-4">
          These page pairs may be competing for the same keywords. Consider consolidating or differentiating their content.
        </p>

        <div className="space-y-4">
          {pairs.map((pair, index) => (
            <div key={pair.id || index} className="border border-surface-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-surface-500 mb-1">Page 1</p>
                    <Link
                      to={`/projects/${projectId}/pages/${pair.pageId1}`}
                      className="text-sm font-medium text-brand-600 hover:text-brand-700 line-clamp-1"
                    >
                      {pair.page1?.title || getUrlPath(pair.page1?.url || '')}
                    </Link>
                    <p className="text-xs text-surface-400 mt-1 line-clamp-1">
                      {getUrlPath(pair.page1?.url || '')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-surface-500 mb-1">Page 2</p>
                    <Link
                      to={`/projects/${projectId}/pages/${pair.pageId2}`}
                      className="text-sm font-medium text-brand-600 hover:text-brand-700 line-clamp-1"
                    >
                      {pair.page2?.title || getUrlPath(pair.page2?.url || '')}
                    </Link>
                    <p className="text-xs text-surface-400 mt-1 line-clamp-1">
                      {getUrlPath(pair.page2?.url || '')}
                    </p>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className={clsx(
                    'text-xl font-bold',
                    pair.similarityScore >= 0.7 ? 'text-red-600' :
                    pair.similarityScore >= 0.5 ? 'text-yellow-600' : 'text-surface-600'
                  )}>
                    {Math.round(pair.similarityScore * 100)}%
                  </p>
                  <p className="text-xs text-surface-500">Similarity</p>
                </div>
              </div>

              {pair.sharedKeywords && pair.sharedKeywords.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-surface-500 mb-1">Shared Keywords:</p>
                  <div className="flex flex-wrap gap-1">
                    {pair.sharedKeywords.slice(0, 8).map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-surface-100 text-surface-600 text-xs rounded">
                        {kw}
                      </span>
                    ))}
                    {pair.sharedKeywords.length > 8 && (
                      <span className="px-2 py-0.5 text-surface-500 text-xs">
                        +{pair.sharedKeywords.length - 8} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {pair.recommendation && (
                <p className="text-sm text-surface-600 bg-surface-50 rounded p-2">
                  {pair.recommendation}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExportTab({
  onExport,
  projectName,
}: {
  onExport: (format: 'json' | 'csv') => void;
  projectName: string;
}) {
  return (
    <div className="card">
      <h3 className="font-semibold text-surface-900 mb-4">Export Audit Data</h3>
      <p className="text-surface-500 mb-6">
        Download the complete audit data for "{projectName}" in your preferred format.
      </p>

      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => onExport('json')}
          className="p-6 border border-surface-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors text-left"
        >
          <Code className="w-8 h-8 text-brand-600 mb-3" />
          <h4 className="font-semibold text-surface-900">JSON Export</h4>
          <p className="text-sm text-surface-500 mt-1">
            Complete structured data including all pages, issues, and cannibalization data.
          </p>
        </button>

        <button
          onClick={() => onExport('csv')}
          className="p-6 border border-surface-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-colors text-left"
        >
          <FileText className="w-8 h-8 text-brand-600 mb-3" />
          <h4 className="font-semibold text-surface-900">CSV Export</h4>
          <p className="text-sm text-surface-500 mt-1">
            Spreadsheet-friendly format with page data, perfect for further analysis.
          </p>
        </button>
      </div>
    </div>
  );
}
